import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Play, Gamepad2, Car, Bike, Film, ArrowUpRight, Loader2, Eye, Zap, Music3Icon, Music, Music2, Music3, Music4, CarIcon } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useLightbox } from "./VideoLightbox";

const CATS = [
  { id: "all", label: "All Drops", icon: Film },
  { id: "gaming", label: "Gaming", icon: Gamepad2 },
  { id: "pov", label: "POV Drives", icon: Car },
  { id: "music", label: "Music", icon: Music4 },
  { id: "mtb", label: "MTB", icon: Bike },
  { id: "shorts", label: "Shorts", icon: Zap },
];

function VideoCard({ v, large = false }) {
  const { open } = useLightbox();
  return (
    <button
      type="button"
      onClick={() => open(v)}
      data-testid={`video-card-${v.video_id}`}
      className={`group relative overflow-hidden bg-[#0A0A0A] border border-white/5 hover:border-[#C41E3A]/60 transition-colors block w-full text-left ${
        large ? "aspect-[16/10]" : "aspect-video"
      }`}
    >
      <img
        src={v.thumbnail_hq}
        onError={(e) => {
          e.currentTarget.src = v.thumbnail;
        }}
        alt={v.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-[#C41E3A]/15 transition-colors duration-300" />

      {/* Top tag */}
      <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap z-10">
        <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] bg-black/70 backdrop-blur px-2.5 py-1 text-white border border-white/10">
          {v.category === "other" ? "Drop" : v.category}
        </span>
        {v.is_short && (
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em] bg-[#C41E3A] text-white px-2.5 py-1">
            Short
          </span>
        )}
        {v.duration && (
          <span className="font-mono-tech text-[10px] tracking-wider bg-black/70 backdrop-blur text-white px-2 py-1 border border-white/10">
            {v.duration}
          </span>
        )}
      </div>

      {/* Play button */}
      <div className="absolute top-4 right-4 w-12 h-12 bg-[#C41E3A] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
        <Play className="w-5 h-5 text-white fill-current" />
      </div>

      {/* Bottom info */}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3
          className={`font-display uppercase text-white leading-tight tracking-wide ${
            large ? "text-2xl md:text-4xl" : "text-lg md:text-xl"
          } line-clamp-2`}
        >
          {v.title}
        </h3>
        <div className="mt-3 flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.25em] text-white/60 font-mono-tech">
          <span className="flex items-center gap-3">
            <span>{new Date(v.published).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })}</span>
            {v.view_count_short && (
              <span className="hidden sm:inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> {v.view_count_short}
              </span>
            )}
          </span>
          <span className="flex items-center gap-1 text-white group-hover:text-[#C41E3A] transition-colors">
            Watch <ArrowUpRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </button>
  );
}

export default function VideosSection({ videos, loading, error }) {
  const [active, setActive] = useState("all");

  const filtered = useMemo(() => {
    if (active === "all") return videos;
    if (active === "shorts") return videos.filter((v) => v.is_short);
    return videos.filter((v) => v.category === active);
  }, [active, videos]);

  return (
    <section
      id="videos"
      data-testid="videos-section"
      className="relative py-24 md:py-36 bg-[#050505]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#C41E3A] mb-4 flex items-center gap-3">
              <span className="w-10 h-px bg-[#C41E3A]" /> The Feed
            </p>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.9]">
              Latest <span className="text-[#C41E3A]">Drops</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-sm md:text-base">
            Auto-fed from the ARSOV YouTube channel. New uploads land here the
            moment they go live — no refresh required.
          </p>
        </div>

        <Tabs value={active} onValueChange={setActive} data-testid="videos-tabs">
          <TabsList className="bg-transparent p-0 h-auto flex flex-wrap gap-2 mb-12 border-b border-white/10 rounded-none w-full justify-start">
            {CATS.map((c) => {
              const Icon = c.icon;
              return (
                <TabsTrigger
                  key={c.id}
                  value={c.id}
                  data-testid={`videos-tab-${c.id}`}
                  className="rounded-none bg-transparent px-5 py-4 text-xs uppercase tracking-[0.25em] text-white/60 data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-[#C41E3A] transition-colors flex items-center gap-2"
                >
                  <Icon className="w-3.5 h-3.5" /> {c.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value={active} className="mt-0">
            {loading && (
              <div className="flex items-center gap-3 text-white/60 py-20" data-testid="videos-loading">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="font-mono-tech text-xs uppercase tracking-[0.3em]">
                  Loading drops…
                </span>
              </div>
            )}

            {error && !loading && (
              <div className="text-white/60 py-20 font-mono-tech text-sm" data-testid="videos-error">
                {error}
              </div>
            )}

            {!loading && !error && filtered.length === 0 && (
              <div className="text-white/60 py-20 font-mono-tech text-xs uppercase tracking-[0.3em]" data-testid="videos-empty">
                No videos in this category yet — check back soon.
              </div>
            )}

            {!loading && !error && filtered.length > 0 && (
              <motion.div
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: { transition: { staggerChildren: 0.06 } },
                }}
                className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6"
                data-testid="videos-grid"
              >
                {filtered.map((v, idx) => (
                  <motion.div
                    key={v.video_id}
                    variants={{
                      hidden: { opacity: 0, y: 30 },
                      show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                    }}
                    className={
                      idx === 0
                        ? "md:col-span-8 md:row-span-2"
                        : "md:col-span-4"
                    }
                  >
                    <VideoCard v={v} large={idx === 0} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
